import { OPTIONS_UPDATE_EVENT, SETTINGS_INDENT_KEY } from "../../lib/constants.js";
import { stringifyDocValue } from "../../lib/parse.js";
import _sfc_main$1 from "./code_editor_tag_attributes.vue.js";
import _sfc_main$2 from "./code_editor_element.vue.js";
import _sfc_main$3 from "./code_editor_slot.vue.js";
/* empty css                                                */
import { Fragment, computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, createTextVNode, createVNode, normalizeClass, openBlock, provide, ref, renderList, resolveDynamicComponent, toDisplayString, unref, withCtx } from "vue";
import { DtButton, DtStack } from "@dialpad/dialtone-vue";
import { DtIconBraces, DtIconCheck, DtIconCopy } from "@dialpad/dialtone-icons/vue";
import { camelCase, paramCase } from "change-case";
//#region src/components/code_editor/code_editor.vue
var _hoisted_1 = { class: "d-fl1" };
var _hoisted_2 = { class: "dtc-theme-popover" };
var _sfc_main = /* @__PURE__ */ Object.assign({ name: "DtcCodeEditor" }, {
	props: {
		options: {
			type: Object,
			required: true
		},
		info: {
			type: Object,
			required: true
		},
		theme: {
			type: String,
			required: true
		},
		verbose: {
			type: Boolean,
			required: true
		},
		indentSpaces: {
			type: Number,
			required: true
		},
		disabledMembers: {
			type: Set,
			default: () => /* @__PURE__ */ new Set()
		},
		devMode: {
			type: Boolean,
			default: false
		},
		hasChanges: {
			type: Boolean,
			default: false
		}
	},
	emits: [OPTIONS_UPDATE_EVENT],
	setup(__props, { emit: __emit }) {
		const props = __props;
		const emit = __emit;
		provide(SETTINGS_INDENT_KEY, computed(() => {
			return props.indentSpaces;
		}));
		const tagName = computed(() => paramCase(props.info.displayName));
		const slotBindingsMap = computed(() => {
			if (!props.info.slots) return {};
			return Object.fromEntries(props.info.slots.filter((slot) => slot.bindings?.length).map((slot) => [slot.name, slot.bindings]));
		});
		const hasSlotContent = computed(() => {
			return props.options.slots ? Object.values(props.options.slots).every((slot) => !slot) : false;
		});
		const copied = ref(false);
		const copiedJson = ref(false);
		/**
		* Generates plain-text copy of the code from component data.
		* Bypasses DOM/innerText entirely to avoid flex layout indentation issues.
		*/
		const copyText = computed(() => {
			const pad = " ".repeat(props.indentSpaces);
			const name = tagName.value;
			const lines = [];
			const infoBindings = props.info.bindings.get();
			const optionBindings = props.options.bindings.get();
			const attrLines = Object.entries(optionBindings).map(([bindingName, value]) => {
				return {
					...infoBindings.find((b) => b.name === bindingName),
					name: bindingName,
					value
				};
			}).filter((member) => {
				if (props.disabledMembers.has(member.name)) return false;
				if (member.value === null || typeof member.value === "number" && isNaN(member.value)) return false;
				if (props.verbose) return true;
				return JSON.stringify(member.defaultValue) !== JSON.stringify(member.value);
			}).map((member) => {
				const v = member.value;
				if (v === true) return pad + member.label;
				const isBind = typeof v !== "string";
				const prefix = isBind ? ":" : "";
				const str = isBind ? stringifyDocValue(v) : v.toString();
				return pad + prefix + member.label + "=\"" + str + "\"";
			});
			const slotEntries = props.options.slots ? Object.entries(props.options.slots).filter(([slot, value]) => value && !props.disabledMembers.has(slot)) : [];
			const hasContent = slotEntries.length > 0;
			if (attrLines.length === 0 && !hasContent) lines.push("<" + name + " />");
			else if (attrLines.length === 0) lines.push("<" + name + ">");
			else {
				lines.push("<" + name);
				lines.push(...attrLines);
				lines.push(hasContent ? ">" : "/>");
			}
			if (hasContent) {
				for (const [slot, value] of slotEntries) if (slot === "default") lines.push(pad + value);
				else {
					const usedBindings = ((props.info.slots?.find((s) => s.name === slot))?.bindings || []).map((b) => camelCase(b.name)).filter((n) => value.includes(n));
					const scope = usedBindings.length > 0 ? "=\"{ " + usedBindings.join(", ") + " }\"" : "";
					lines.push(pad + "<template #" + slot + scope + ">");
					lines.push(pad + pad + value);
					lines.push(pad + "</template>");
				}
				lines.push("</" + name + ">");
			}
			return lines.join("\n");
		});
		function serializeAsVariantPreset() {
			const preset = {};
			for (const group of [
				"props",
				"slots",
				"attributes"
			]) {
				const infoMembers = props.info[group];
				const optionValues = props.options[group];
				if (!infoMembers || !optionValues) continue;
				for (const member of infoMembers) {
					const value = optionValues[member.name];
					if (props.disabledMembers.has(member.name)) continue;
					if (value === void 0 || value === null || value === "") continue;
					if (!preset[group]) preset[group] = {};
					preset[group][member.name] = { initialValue: value };
				}
			}
			return preset;
		}
		function formatJsValue(value, indent) {
			if (typeof value === "string") return `'${value.replace(/'/g, "\\'")}'`;
			if (typeof value === "boolean" || typeof value === "number") return String(value);
			if (Array.isArray(value)) return JSON.stringify(value);
			if (typeof value === "object" && value !== null) {
				const entries = Object.entries(value);
				if (entries.length === 0) return "{}";
				return `{\n${entries.map(([k, v]) => `${indent}  ${k}: ${formatJsValue(v, indent + "  ")}`).join(",\n")},\n${indent}}`;
			}
			return String(value);
		}
		function formatPresetAsJs(preset) {
			const pad = "  ";
			const sections = [];
			for (const [group, members] of Object.entries(preset)) {
				const indent = pad + pad + pad;
				const entries = Object.entries(members).map(([name, obj]) => {
					return `${indent}${name}: { initialValue: ${formatJsValue(obj.initialValue, indent)} }`;
				}).join(",\n");
				sections.push(`${pad}${pad}${group}: {\n${entries},\n${pad}${pad}}`);
			}
			return `\n${pad}'⚡️ UNNAMED PRESET': {\n${sections.join(",\n")},\n${pad}},`;
		}
		async function copyJson() {
			const text = formatPresetAsJs(serializeAsVariantPreset());
			await navigator.clipboard.writeText(text);
			copiedJson.value = true;
			await new Promise((resolve) => setTimeout(resolve, 2e3));
			copiedJson.value = false;
		}
		async function copy() {
			await navigator.clipboard.writeText(copyText.value);
			copied.value = true;
			await new Promise((resolve) => setTimeout(resolve, 2e3));
			copied.value = false;
		}
		return (_ctx, _cache) => {
			return openBlock(), createBlock(unref(DtStack), {
				ref: "editor",
				class: "dtc-code-editor d-w100p",
				direction: "row",
				gap: "300",
				align: "start"
			}, {
				default: withCtx(() => [createElementVNode("div", _hoisted_1, [createVNode(_sfc_main$2, {
					"tag-name": tagName.value,
					"self-closing": hasSlotContent.value
				}, {
					opening: withCtx(() => [createVNode(_sfc_main$1, {
						"info-bindings": __props.info.bindings.get(),
						"option-bindings": __props.options.bindings.get(),
						verbose: __props.verbose,
						"disabled-members": __props.disabledMembers
					}, null, 8, [
						"info-bindings",
						"option-bindings",
						"verbose",
						"disabled-members"
					])]),
					default: withCtx(() => [(openBlock(true), createElementBlock(Fragment, null, renderList(__props.options.slots, (value, slot) => {
						return openBlock(), createElementBlock(Fragment, { key: slot }, [value && !__props.disabledMembers.has(slot) ? (openBlock(), createBlock(_sfc_main$3, {
							key: 0,
							name: slot,
							bindings: slotBindingsMap.value[slot],
							content: value,
							"onUpdate:options": _cache[0] || (_cache[0] = (e) => emit(unref("update:options"), e))
						}, {
							default: withCtx(() => [createElementVNode("span", _hoisted_2, toDisplayString(value), 1)]),
							_: 2
						}, 1032, [
							"name",
							"bindings",
							"content"
						])) : createCommentVNode("", true)], 64);
					}), 128))]),
					_: 1
				}, 8, ["tag-name", "self-closing"])]), createVNode(unref(DtStack), {
					direction: "row",
					gap: "200",
					class: "d-ps-sticky d-ibs-0 d-iie-0"
				}, {
					default: withCtx(() => [__props.devMode && __props.hasChanges ? (openBlock(), createBlock(unref(DtButton), {
						key: 0,
						class: "dtc-theme__button",
						kind: "muted",
						importance: "clear",
						size: 100,
						onClick: copyJson
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(copiedJson.value ? "Copied!" : "Copy\xA0JSON"), 1)]),
						icon: withCtx(({ iconSize }) => [(openBlock(), createBlock(resolveDynamicComponent(copiedJson.value ? unref(DtIconCheck) : unref(DtIconBraces)), {
							size: iconSize,
							class: normalizeClass({ "d-fc-positive": copiedJson.value })
						}, null, 8, ["size", "class"]))]),
						_: 1
					})) : createCommentVNode("", true), createVNode(unref(DtButton), {
						class: "dtc-theme__button",
						kind: "muted",
						importance: "clear",
						size: 100,
						onClick: copy
					}, {
						default: withCtx(() => [createTextVNode(toDisplayString(copied.value ? "Copied!" : "Copy"), 1)]),
						icon: withCtx(({ iconSize }) => [(openBlock(), createBlock(resolveDynamicComponent(copied.value ? unref(DtIconCheck) : unref(DtIconCopy)), {
							size: iconSize,
							class: normalizeClass({ "d-fc-positive": copied.value })
						}, null, 8, ["size", "class"]))]),
						_: 1
					})]),
					_: 1
				})]),
				_: 1
			}, 512);
		};
	}
});
//#endregion
export { _sfc_main as default };

//# sourceMappingURL=code_editor.vue.js.map